/**
 * Gemeinsamer Druck-Helfer für die Theken-QR-Karten („klicken zum Drucken").
 * Kopf: händlerspezifisches Firmenlogo (Fallback: Firmenname). Danach Titel,
 * Hinweis, QR-Code, Scan-URL. Fuß: Filialadresse, Telefon & Öffnungszeiten —
 * damit der Ausdruck als vollwertiger Kunden-Aushang (z. B. Schaufenster für
 * Selbstannahme außerhalb der Öffnungszeiten) taugt.
 */
function escapeHtml(str = "") {
    return String(str)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function absoluteUrl(url = "") {
    if (!url) return "";
    return url.startsWith("http") ? url : `${window.location.origin}${url}`;
}

/** Aus dem eingeloggten Nutzer/Händler die Druck-relevanten Firmendaten ableiten. */
export function dealerPrintInfo(user = {}) {
    const city = String(user.city || "").trim();
    const plz = String(user.postal_code || "").trim();
    const street = String(user.street || user.address || "").trim();
    const line2 = [plz, city].filter(Boolean).join(" ").trim();
    const address = [street, line2].filter(Boolean).join(", ");
    const phone = user.phone_office || user.collective_phone || user.phone || "";
    const hours = user.sales_hours_enabled ? (user.sales_hours || "") : "";
    return {
        logoUrl: user.company_logo_url || user.logo_url || "",
        companyName: user.company_name || "",
        address,
        phone,
        hours,
    };
}

/**
 * Öffnet ein Druckfenster mit gebrandetem QR-Aushang.
 * @param {Object} opts
 * @param {string} opts.title      Überschrift
 * @param {string} opts.subtitle   Hinweistext
 * @param {string} opts.scanUrl    Ziel-URL des QR-Codes
 * @param {string} [opts.logoUrl]  Händlerlogo (company_logo_url)
 * @param {string} [opts.companyName] Firmenname (Fallback + Fußzeile)
 * @param {string} [opts.address]  Filialadresse (Fußbereich)
 * @param {string} [opts.phone]    Telefon (Fußbereich)
 * @param {string} [opts.hours]    Öffnungszeiten, mehrzeilig (Fußbereich)
 */
export async function openQrPrint({ title, subtitle, scanUrl, logoUrl, companyName, address, phone, hours }) {
    if (!scanUrl) return false;
    const QRCode = (await import("qrcode")).default;
    const dataUrl = await QRCode.toDataURL(scanUrl, { width: 640, margin: 2 });
    const w = window.open("", "_blank", "width=520,height=820");
    if (!w) return false;

    const logo = absoluteUrl(logoUrl);
    const brandBlock = logo
        ? `<img class="logo" src="${escapeHtml(logo)}" alt="${escapeHtml(companyName || "Logo")}" />`
        : (companyName ? `<div class="cn">${escapeHtml(companyName)}</div>` : "");

    const hoursHtml = hours
        ? `<div class="hrs"><div class="hl">Öffnungszeiten</div>${escapeHtml(hours).replace(/\n/g, "<br/>")}</div>`
        : "";
    const footParts = [];
    if (companyName) footParts.push(`<b>${escapeHtml(companyName)}</b>`);
    if (address) footParts.push(escapeHtml(address));
    if (phone) footParts.push(`Tel. ${escapeHtml(phone)}`);
    const footHtml = (footParts.length || hoursHtml)
        ? `<div class="foot">${footParts.join(" · ")}${hoursHtml}</div>`
        : "";

    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;text-align:center;padding:28px 24px;color:#111827}
  .logo{max-height:64px;max-width:260px;object-fit:contain;margin:0 auto 14px;display:block}
  .cn{font-size:18px;font-weight:800;margin-bottom:12px}
  h1{font-size:20px;margin:8px 0}
  p{font-size:13px;color:#374151;margin:6px auto;max-width:360px}
  .qr{width:320px;height:320px;margin:8px auto}
  .u{font-size:11px;color:#6b7280;word-break:break-all}
  .foot{margin-top:22px;padding-top:14px;border-top:1px solid #e5e7eb;font-size:12px;color:#374151;line-height:1.5}
  .hrs{margin-top:10px;font-size:12px;color:#374151}
  .hl{font-weight:700;text-transform:uppercase;letter-spacing:.04em;font-size:10px;color:#6b7280;margin-bottom:2px}
</style></head>
<body>
  ${brandBlock}
  <h1>${escapeHtml(title)}</h1>
  <p>${escapeHtml(subtitle)}</p>
  <img class="qr" src="${dataUrl}" alt="QR" />
  <p class="u">${escapeHtml(scanUrl)}</p>
  ${footHtml}
  <script>window.onload=function(){window.print();}<\/script>
</body></html>`);
    w.document.close();
    return true;
}

/* ── Aug-2026 (User-Wunsch): QR-Druck auf Etikettenbögen (A4) ──────────────
   Gängige Bogen-Formate + eigene Maße. Das Druck-Layout rechnet Spalten/Zeilen
   aus der Etikettengröße und zentriert das Raster auf dem A4-Bogen. */
export const LABEL_FORMATS = [
    { id: "70x37", name: "70 × 37 mm — 24 St./Bogen (z. B. Avery 3474)", w: 70, h: 37 },
    { id: "70x36", name: "70 × 36 mm — 24 St./Bogen (z. B. Avery 3490)", w: 70, h: 36 },
    { id: "63.5x38.1", name: "63,5 × 38,1 mm — 21 St./Bogen (z. B. Avery L7160)", w: 63.5, h: 38.1 },
    { id: "52.5x29.7", name: "52,5 × 29,7 mm — 40 St./Bogen (z. B. Avery 3651)", w: 52.5, h: 29.7 },
    { id: "99.1x38.1", name: "99,1 × 38,1 mm — 14 St./Bogen (z. B. Avery L7163)", w: 99.1, h: 38.1 },
    { id: "48.5x25.4", name: "48,5 × 25,4 mm — 44 St./Bogen (z. B. Avery 3657)", w: 48.5, h: 25.4 },
];

export async function openQrLabelPrint({ scanUrl, label = "", widthMm, heightMm, count }) {
    const wMm = Number(widthMm), hMm = Number(heightMm);
    if (!scanUrl || !wMm || !hMm || wMm < 15 || hMm < 15 || wMm > 210 || hMm > 297) return false;
    const QRCode = (await import("qrcode")).default;
    const dataUrl = await QRCode.toDataURL(scanUrl, { width: 480, margin: 1 });
    const pageW = 210, pageH = 297;
    const cols = Math.max(1, Math.floor(pageW / wMm));
    const rows = Math.max(1, Math.floor(pageH / hMm));
    const perSheet = cols * rows;
    const total = Math.max(1, Math.min(Number(count) || perSheet, perSheet * 10));
    const marginX = Math.max(0, (pageW - cols * wMm) / 2);
    const marginY = Math.max(0, (pageH - rows * hMm) / 2);
    const qrSide = Math.max(10, Math.min(hMm - 4, label ? wMm * 0.5 : wMm - 4));
    const cells = Array.from({ length: total }, () => `
      <div class="cell">
        <img src="${dataUrl}" alt="QR"/>
        ${label ? `<div class="lt">${escapeHtml(label)}</div>` : ""}
      </div>`).join("");
    const w = window.open("", "_blank", "width=780,height=920");
    if (!w) return false;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>QR-Etiketten ${wMm}×${hMm} mm</title>
<style>
  @page { size: A4; margin: ${marginY.toFixed(2)}mm ${marginX.toFixed(2)}mm; }
  html,body { margin:0; padding:0; }
  .sheet { display:grid; grid-template-columns: repeat(${cols}, ${wMm}mm); grid-auto-rows: ${hMm}mm; }
  .cell { display:flex; align-items:center; justify-content:center; gap:1.5mm;
          overflow:hidden; padding:1.5mm; box-sizing:border-box; }
  .cell img { width:${qrSide.toFixed(2)}mm; height:${qrSide.toFixed(2)}mm; }
  .lt { font-family:Arial,Helvetica,sans-serif; font-size:7pt; font-weight:700;
        color:#111827; line-height:1.25; word-break:break-word; max-width:${(wMm - qrSide - 5).toFixed(2)}mm; }
</style></head>
<body>
  <div class="sheet">${cells}</div>
  <script>window.onload=function(){window.print();}<\/script>
</body></html>`);
    w.document.close();
    return true;
}
